import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

import ComboBox from '../Fundamentals/ComboBox';
import { Checkbox as CheckboxJS } from '../FormComponents/Checkbox';
import { LabelPosition, SwitchSize, SwitchToggle as SwitchToggleJS } from '../FormComponents/SwitchToggle';
import FilterCard from './FilterCard';
import type { FilterOption } from './PriceSimulation.types';

interface CheckboxProps {
    id: string;
    label?: string | null;
    checked: boolean;
    onChange: (checked: boolean, id: string) => void;
    disabled?: boolean;
}

const Checkbox = CheckboxJS as unknown as ComponentType<CheckboxProps>;

interface SwitchToggleProps {
    id: string;
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    size?: string;
    labelPosition?: string;
}

const SwitchToggle = SwitchToggleJS as unknown as ComponentType<SwitchToggleProps>;

interface PricingFilterBarProps {
    productFilters: FilterOption[];
    onProductFiltersChange: (options: FilterOption[]) => void;
    selectedPackages: FilterOption[];
    onPackagesChange: (options: FilterOption[]) => void;
    selectedProductTypes: FilterOption[];
    onProductTypesChange: (options: FilterOption[]) => void;
    selectedTiers: FilterOption[];
    onTiersChange: (options: FilterOption[]) => void;
    selectedPlans: FilterOption[];
    onPlansChange: (options: FilterOption[]) => void;
    modificationStatuses: FilterOption[];
    onModificationStatusesChange: (options: FilterOption[]) => void;
    onlyPlansWithVolume: boolean;
    onOnlyPlansWithVolumeChange: (value: boolean) => void;
    productOptions: FilterOption[];
    packageOptions: FilterOption[];
    productTypeOptions: FilterOption[];
    tierOptions: FilterOption[];
    planOptions: FilterOption[];
    modificationStatusOptions: FilterOption[];
    allPackagesLoaded: boolean;
}

const PricingFilterBar = ({
    productFilters,
    onProductFiltersChange,
    selectedPackages,
    onPackagesChange,
    selectedProductTypes,
    onProductTypesChange,
    selectedTiers,
    onTiersChange,
    selectedPlans,
    onPlansChange,
    modificationStatuses,
    onModificationStatusesChange,
    onlyPlansWithVolume,
    onOnlyPlansWithVolumeChange,
    productOptions,
    packageOptions,
    productTypeOptions,
    tierOptions,
    planOptions,
    modificationStatusOptions,
    allPackagesLoaded,
}: PricingFilterBarProps) => {
    const { t } = useTranslation();

    const handlePlanToggle = (planId: string | number) => {
        const exists = selectedPlans.some((p) => p.id === planId);
        if (exists) {
            onPlansChange(selectedPlans.filter((p) => p.id !== planId));
        } else {
            const plan = planOptions.find((p) => p.id === planId);
            if (plan) onPlansChange([...selectedPlans, plan]);
        }
    };

    const hasSelectedPackages = selectedPackages.length > 0;
    const canFilterWithoutPackages = hasSelectedPackages || allPackagesLoaded;

    return (
        <div className="d-grid grid-cols-1 grid-cols-sm-2 grid-cols-md-3 grid-cols-xxl-6 gap-16 mt-16">
            <FilterCard selectedItems={selectedPackages} onRemoveItem={(item) => onPackagesChange(selectedPackages.filter((p) => p.id !== item.id))}>
                <ComboBox
                    placeholder={t('priceSimulation.package')}
                    options={packageOptions}
                    value={selectedPackages.map((p) => p.id)}
                    onChange={(_values: unknown, options: FilterOption[]) => onPackagesChange(options)}
                    valueKey="id"
                    labelKey="name"
                    isMulti={true}
                    isClearable={true}
                    hideInternalChips={true}
                    showSelectAll={true}
                />
            </FilterCard>

            <FilterCard selectedItems={selectedTiers} onRemoveItem={(item) => onTiersChange(selectedTiers.filter((tier) => tier.id !== item.id))}>
                <ComboBox
                    placeholder={t('priceSimulation.tier')}
                    options={tierOptions}
                    value={selectedTiers.map((tier) => tier.id)}
                    onChange={(_values: unknown, options: FilterOption[]) => onTiersChange(options)}
                    valueKey="id"
                    labelKey="name"
                    isMulti={true}
                    isClearable={true}
                    hideInternalChips={true}
                    disabled={!hasSelectedPackages}
                />
            </FilterCard>

            <FilterCard selectedItems={selectedProductTypes} onRemoveItem={(item) => onProductTypesChange(selectedProductTypes.filter((p) => p.id !== item.id))}>
                <ComboBox
                    placeholder={t('priceSimulation.productType')}
                    options={productTypeOptions}
                    value={selectedProductTypes.map((p) => p.id)}
                    onChange={(_values: unknown, options: FilterOption[]) => onProductTypesChange(options)}
                    valueKey="id"
                    labelKey="name"
                    isMulti={true}
                    isClearable={true}
                    hideInternalChips={true}
                    disabled={!canFilterWithoutPackages}
                />
            </FilterCard>

            <FilterCard selectedItems={productFilters} onRemoveItem={(item) => onProductFiltersChange(productFilters.filter((p) => p.id !== item.id))}>
                <ComboBox
                    placeholder={t('priceSimulation.product')}
                    options={productOptions}
                    value={productFilters.map((p) => p.id)}
                    onChange={(_values: unknown, options: FilterOption[]) => onProductFiltersChange(options)}
                    valueKey="id"
                    labelKey="name"
                    renderOption={(option) => (
                        <>
                            <div className="d-block text-truncate">{option.label}</div>
                            {option.description !== undefined && <div className="d-block body-xs text-secondary text-truncate">{option.description}</div>}
                        </>
                    )}
                    isMulti={true}
                    isClearable={true}
                    hideInternalChips={true}
                    disabled={!canFilterWithoutPackages}
                />
            </FilterCard>

            <FilterCard
                selectedItems={selectedPlans}
                onRemoveItem={(item) => onPlansChange(selectedPlans.filter((p) => p.id !== item.id))}
                renderCustomBody={() => (
                    <div className="d-flex flex-column gap-8 w-100">
                        <div className="d-flex flex-column gap-8" role="group" aria-label={t('priceSimulation.plan') ?? ''}>
                            {planOptions.map((plan) => (
                                <Checkbox
                                    key={plan.id}
                                    label={plan.name}
                                    checked={selectedPlans.some((p) => p.id === plan.id)}
                                    onChange={() => handlePlanToggle(plan.id)}
                                    id={`plan-${plan.id}`}
                                    disabled={!canFilterWithoutPackages}
                                />
                            ))}
                        </div>
                        <div className="border-top pt-8">
                            <SwitchToggle
                                id="only-plans-with-volume"
                                label={t('priceSimulation.onlyPlansWithVolume')}
                                checked={onlyPlansWithVolume}
                                onChange={onOnlyPlansWithVolumeChange}
                                disabled={!hasSelectedPackages}
                                size={SwitchSize.SMALL}
                                labelPosition={LabelPosition.RIGHT}
                            />
                        </div>
                    </div>
                )}
            >
                {/* An inert input, not a div, so the design system's input and disabled styling matches the ComboBoxes beside it.
                    Not readOnly: the design system greys read-only inputs out like disabled ones. */}
                <input type="text" className="form-control rounded-8 w-100" placeholder={t('priceSimulation.plan') ?? ''} inert disabled={!canFilterWithoutPackages} />
            </FilterCard>

            <FilterCard selectedItems={modificationStatuses} onRemoveItem={(item) => onModificationStatusesChange(modificationStatuses.filter((s) => s.id !== item.id))}>
                <ComboBox
                    placeholder={t('priceSimulation.modificationStatus')}
                    options={modificationStatusOptions}
                    value={modificationStatuses.map((s) => s.id)}
                    onChange={(_values: unknown, options: FilterOption[]) => onModificationStatusesChange(options)}
                    valueKey="id"
                    labelKey="name"
                    isMulti={true}
                    isClearable={true}
                    hideInternalChips={true}
                    disabled={!canFilterWithoutPackages}
                />
            </FilterCard>
        </div>
    );
};

export default PricingFilterBar;
